/*
 * upload 上传
 * author zhousheng
 */
import * as React from 'react';
import CSSMotion from 'rc-motion';
import classNames from 'classnames';
import EyeOutlined from 'acud-icon/lib/icons/OutlinedEye';
import DeleteOutlined from 'acud-icon/lib/icons/OutlinedDelete';
import OutlinedClose from 'acud-icon/lib/icons/OutlinedClose';
import DownloadOutlined from 'acud-icon/lib/icons/OutlinedCloudDownload';
import OutlinedButtonUpload from 'acud-icon/lib/icons/OutlinedButtonUpload';
import Tooltip from '../../tooltip';
import Progress from '../../progress';
import { ConfigContext } from '../../config-provider';
const ListItem = React.forwardRef(({ prefixCls, className, style, locale, listType, file, items, progress: progressProps, iconRender, actionIconRender, itemRender, isImgUrl, motionDeadline = 2000, showPreviewIcon, showRemoveIcon, showDownloadIcon, showUploadIcon: internalShowUploadIcon, removeIcon: customRemoveIcon, uploadIcon: customUploadIcon, downloadIcon: customDownloadIcon, onPreview, onDownload, onClose, onUpload, }, ref) => {
    var _a, _b;
    const { status } = file;
    const [mergedStatus, setMergedStatus] = React.useState(status);
    React.useEffect(() => {
        if (status !== 'removed') {
            setMergedStatus(status);
        }
    }, [status]);
    // Delay to show the progress bar
    const [showProgress, setShowProgress] = React.useState(false);
    // 外部传入的file 无法重传，根据originFileObj来判断是本地选中的
    const showUploadIcon = React.useMemo(() => !!(file === null || file === void 0 ? void 0 : file.originFileObj) && internalShowUploadIcon, [file, internalShowUploadIcon]);
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setShowProgress(true);
        }, 300);
        return () => {
            window.clearTimeout(timer);
        };
    }, []);
    // This is used for legacy span make scrollHeight the wrong value.
    // We will force these to be `display: block` with non `picture-card`
    const spanClassName = `${prefixCls}-span`;
    const iconNode = iconRender(file);
    let icon = <div className={`${prefixCls}-text-icon`}>{iconNode}</div>;
    if (listType === 'picture' || listType === 'picture-card') {
        if (mergedStatus === 'uploading' || (!file.thumbUrl && !file.url)) {
            const uploadingClassName = classNames({
                [`${prefixCls}-list-item-thumbnail`]: true,
                [`${prefixCls}-list-item-file`]: mergedStatus !== 'uploading',
            });
            icon = <div className={uploadingClassName}>{iconNode}</div>;
        }
        else {
            const thumbnail = (isImgUrl === null || isImgUrl === void 0 ? void 0 : isImgUrl(file)) ? (<img src={file.thumbUrl || file.url} alt={file.name} className={`${prefixCls}-list-item-image`}/>) : (iconNode);
            const aClassName = classNames({
                [`${prefixCls}-list-item-thumbnail`]: true,
                [`${prefixCls}-list-item-file`]: isImgUrl && !isImgUrl(file),
            });
            icon = (<a className={aClassName} onClick={e => onPreview(file, e)} href={file.url || file.thumbUrl} target="_blank" rel="noopener noreferrer">
                        {thumbnail}
                    </a>);
        }
    }
    const infoUploadingClass = classNames({
        [`${prefixCls}-list-item`]: true,
        [`${prefixCls}-list-item-${mergedStatus}`]: true,
        [`${prefixCls}-list-item-list-type-${listType}`]: true,
    });
    const linkProps = typeof file.linkProps === 'string'
        ? JSON.parse(file.linkProps)
        : file.linkProps;
    const removeIcon = showRemoveIcon
        ? actionIconRender((typeof customRemoveIcon === 'function'
            ? customRemoveIcon(file)
            : customRemoveIcon) || (listType === 'text' ? (<OutlinedClose width={16}/>) : (<DeleteOutlined width={16}/>)), () => onClose(file), prefixCls, locale.removeFile)
        : null;
    const downloadIcon = showDownloadIcon
        ? actionIconRender((typeof customDownloadIcon === 'function'
            ? customDownloadIcon(file)
            : customDownloadIcon) || <DownloadOutlined />, () => onDownload(file), prefixCls, locale.downloadFile)
        : null;
    // 下载icon
    const downloadOrDelete = listType !== 'picture-card' && (<span key="download-delete" className={classNames(`${prefixCls}-list-item-card-actions`, {
            picture: listType === 'picture',
        })}>
                {downloadIcon}
                {removeIcon}
            </span>);
    const listItemNameClass = classNames(`${prefixCls}-list-item-name`);
    const preview = file.url
        ? [
            <a key="view" target="_blank" rel="noopener noreferrer" className={listItemNameClass} title={file.name} {...linkProps} href={file.url} onClick={e => onPreview(file, e)}>
                    {file.name}
                </a>,
            downloadOrDelete,
        ]
        : [
            <span key="view" className={listItemNameClass} onClick={e => onPreview(file, e)} title={file.name}>
                    {file.name}
                </span>,
            downloadOrDelete,
        ];
    const previewStyle = {
        pointerEvents: 'none',
        opacity: 0.5,
    };
    // 预览icon
    const previewIcon = showPreviewIcon ? (<a href={file.url || file.thumbUrl} target="_blank" rel="noopener noreferrer" style={file.url || file.thumbUrl ? undefined : previewStyle} onClick={e => onPreview(file, e)} title={locale.previewFile}>
                <EyeOutlined />
            </a>) : null;
    // 上传icon
    const uploadIcon = showUploadIcon
        ? actionIconRender((typeof customUploadIcon === 'function'
            ? customUploadIcon(file)
            : customUploadIcon) || <OutlinedButtonUpload />, () => onUpload(file), prefixCls, locale.uploadFile)
        : null;
    const actions = listType === 'picture-card'
        && mergedStatus !== 'uploading' && (<span className={`${prefixCls}-list-item-actions`}>
                {mergedStatus === 'done' && previewIcon}
                {mergedStatus === 'done' && downloadIcon}
                {['error', 'done'].includes(mergedStatus) && uploadIcon}
                {removeIcon}
            </span>);
    let message;
    if (file.response && typeof file.response === 'string') {
        message = file.response;
    }
    else {
        message
            = ((_a = file.error) === null || _a === void 0 ? void 0 : _a.statusText)
                || ((_b = file.error) === null || _b === void 0 ? void 0 : _b.message)
                || locale.uploadError;
    }
    const iconAndPreview = (<span className={spanClassName}>
                {icon}
                {preview}
            </span>);
    const { getPrefixCls } = React.useContext(ConfigContext);
    const rootPrefixCls = getPrefixCls();
    const dom = (<div className={infoUploadingClass}>
                <div className={`${prefixCls}-list-item-info`}>
                    {iconAndPreview}
                </div>
                {actions}
                {showProgress && (<CSSMotion motionName={`${rootPrefixCls}-fade`} visible={mergedStatus === 'uploading'} 
        // rc-motion 锁了版本2.4.3，但是新版本（2.7.3）已经fix，所以采用key
        // https://github.com/react-component/motion/pull/24
        key={mergedStatus} motionDeadline={motionDeadline}>
                        {({ className: motionClassName }) => {
                // show loading icon if upload progress listener is disabled
                const loadingProgress = 'percent' in file ? (<Progress trailColor="#DADBDD" strokeColor="#2468F2" infoType="none" type="line" percent={file.percent} strokeWidth={listType === 'text' ? 2 : 4} {...progressProps}/>) : null;
                return (<div className={classNames(`${prefixCls}-list-item-progress`, motionClassName)}>
                                    {loadingProgress}
                                </div>);
            }}
                    </CSSMotion>)}
            </div>);
    const listContainerNameClass = classNames(`${prefixCls}-list-${listType}-container`, className);
    const item = mergedStatus === 'error' ? (<Tooltip title={message} overlayClassName="error-tooltip" getPopupContainer={node => node.parentNode}>
                    {dom}
                </Tooltip>) : (dom);
    return (<div className={listContainerNameClass} style={style} ref={ref}>
                {itemRender
            ? itemRender(item, file, items, {
                download: onDownload.bind(null, file),
                preview: onPreview.bind(null, file),
                upload: onUpload.bind(null, file),
                remove: onClose.bind(null, file),
            })
            : item}
            </div>);
});
export default ListItem;
